// src/lib/audio-context-manager.ts
export class AudioContextManager {
  private static instance: AudioContextManager;
  private ctx: AudioContext | null = null;
  private isInitializing = false;

  private constructor() {}

  public static getInstance(): AudioContextManager {
    if (!AudioContextManager.instance) {
      AudioContextManager.instance = new AudioContextManager();
    }
    return AudioContextManager.instance;
  }

  public async getContext(preferredSampleRate = 48000): Promise<AudioContext> {
    if (this.ctx && this.ctx.state !== 'closed') {
      await this.ensureResumed();
      return this.ctx;
    }

    if (this.isInitializing) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.getContext(preferredSampleRate);
    }

    this.isInitializing = true;

    try {
      const AudioCtxClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

      try {
        this.ctx = new AudioCtxClass({
          latencyHint: 'interactive',
          sampleRate: preferredSampleRate,
        });
      } catch {
        this.ctx = new AudioCtxClass({
          latencyHint: 'interactive',
        });
      }

      this.attachStateListeners();
      await this.ensureResumed();
    } finally {
      this.isInitializing = false;
    }

    return this.ctx;
  }

  public async unlockOnUserGesture(): Promise<boolean> {
    const context = await this.getContext();
    if (context.state === 'suspended') {
      await context.resume();
    }
    return context.state === 'running';
  }

  private async ensureResumed(): Promise<void> {
    if (this.ctx && this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch (err) {
        console.warn('AudioContext resume deferred until user interaction:', err);
      }
    }
  }

  private attachStateListeners(): void {
    if (!this.ctx) return;

    this.ctx.onstatechange = () => {
      console.info(`AudioContext State Shift -> ${this.ctx?.state}`);
      window.dispatchEvent(
        new CustomEvent('audiocontext-state-change', {
          detail: { state: this.ctx?.state },
        })
      );
    };

    if (navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = async () => {
        console.info('Audio device change detected. Verifying context state...');
        if (this.ctx && this.ctx.state === 'running') {
          await this.ctx.suspend();
          await this.ctx.resume();
        }
      };
    }
  }

  public async destroy(): Promise<void> {
    if (this.ctx && this.ctx.state !== 'closed') {
      await this.ctx.close();
      this.ctx = null;
    }
  }
}
